import {
    ActivityScheduleRule,
    CommandResponse,
    EntityCommandCreateResponse,
    isRuleDuplicated,
    RevisionedCommandResponse,
    StudyEntity,
    StudyEntityModification,
    StudyEntityType,
    toActivityScheduleRule,
} from '@faro/study-designer-model';
import axios from 'axios';
import { pending } from '../../package/optimistic-udpates';
import { createStudyEntityHookFactory } from '../createStudyEntityHookFactory';
import { ScheduleOfActivitiesQueryParameter, StudyPathParameter, VariantQueryParameter } from '../parameter';

const factory = createStudyEntityHookFactory<ActivityScheduleRule>(StudyEntityType.ActivityScheduleRule);

type ActivityScheduleRulePathParameters = StudyPathParameter;
type ActivityScheduleRuleQueryParameters = ScheduleOfActivitiesQueryParameter & VariantQueryParameter;

export const useActivityScheduleRules = factory.list(
    (
        { studyId, ...query }: ActivityScheduleRulePathParameters & ActivityScheduleRuleQueryParameters,
        signal?: AbortSignal
    ): Promise<ActivityScheduleRule[]> =>
        axios
            .get(`/api/study-designer/studies/${studyId}/activity-schedule-rules`, { signal, params: query })
            .then(response => response.data?.map(toActivityScheduleRule) ?? [])
);

export const useInsertActivityScheduleRules = factory.mutate(
    ({
        studyId,
        ...entityReference
    }: StudyEntityModification<ActivityScheduleRule>): Promise<
        RevisionedCommandResponse<EntityCommandCreateResponse>
    > =>
        axios
            .post(
                `/api/study-designer/studies/${studyId}/activity-schedule-rules/actions/create-batch`,
                entityReference
            )
            .then(response => response.data),
    {
        optimisticUpdate: (modifications, cachedData: ActivityScheduleRule[]): ActivityScheduleRule[] => {
            return [
                ...cachedData,
                ...modifications.entities.reduce((pendingRules, rule) => {
                    const exists = cachedData.some(x => isRuleDuplicated(x, rule));
                    if (!exists) {
                        pendingRules.push(pending(rule));
                    }
                    return pendingRules;
                }, [] as ActivityScheduleRule[]),
            ];
        },
    }
);

export const useUpsertActivityScheduleRules = factory.mutate(
    ({
        studyId,
        ...entityReference
    }: StudyEntityModification<ActivityScheduleRule>): Promise<RevisionedCommandResponse<CommandResponse>> =>
        axios
            .post(`/api/study-designer/studies/${studyId}/activity-schedule-rules/actions/save-batch`, entityReference)
            .then(response => response.data)
);

export const useUpdateActivityScheduleRules = factory.mutate(
    (entity: ActivityScheduleRule): Promise<RevisionedCommandResponse<CommandResponse>> =>
        axios
            .put(`/api/study-designer/studies/${entity.studyId}/activity-schedule-rules/${entity.id}`, entity)
            .then(response => response.data),
    { optimisticUpdate: (rule, cachedData) => cachedData.map(x => (x.id === rule.id ? rule : x)) }
);

export const useDeleteActivityScheduleRule = factory.mutate(
    (entity: Pick<StudyEntity, 'id' | 'studyId'>): Promise<RevisionedCommandResponse<CommandResponse>> =>
        axios
            .delete(`/api/study-designer/studies/${entity.studyId}/activity-schedule-rules/${entity.id}`)
            .then(response => response.data),
    { optimisticUpdate: (rule, cachedData) => cachedData.filter(x => x.id !== rule.id) }
);
